/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { Settings } from '@/data/Settings';
import { User } from '@/data/User';
import { Order } from '@/data/types/Order';
import { getAddPaymentMethodPage } from '@/data/containers/AddPaymentMethodPage';
import { getCreateCheckoutProfilePage } from '@/data/containers/CreateCheckoutProfilePage';
import { getEditCheckoutProfilePage } from '@/data/containers/EditCheckoutProfilePage';
import { getCustomPage } from '@/data/containers/CustomPage';
import { getPaymentMethodPage } from '@/data/containers/PaymentMethodPage';
import { RouteProtection } from '@/data/containers/manifest';
import { IncomingContent } from '@/data/types/IncomingContent';
import { Layout } from '@/data/types/Layout';
import { validateProtectedRoute } from '@/data/utils/validateProtectedRoute';
import { TranslationTable } from 'integration/generated/translations';

const layoutManifest = {
	CreateCheckoutProfilePage: getCreateCheckoutProfilePage,
	EditCheckoutProfilePage: getEditCheckoutProfilePage,
	PaymentMethodPage: getPaymentMethodPage,
	AddPaymentMethodPage: getAddPaymentMethodPage,
	CustomPage: getCustomPage,
};

export const dataContainerManifestCustom: {
	[key: string]: (props: IncomingContent) => Layout;
} = layoutManifest;

type LayoutKeys = keyof typeof layoutManifest;
type LocalRoutes = TranslationTable['Routes'];

/**
 * Layout relationships with translation route name as the key and
 * the layoutManifest key as the value.
 */
export const dataRouteManifestCustom: Record<string, LayoutKeys> = {
	CreateCheckoutProfiles: 'CreateCheckoutProfilePage',
	EditCheckoutProfile: 'EditCheckoutProfilePage',
	PaymentMethod: 'PaymentMethodPage',
	AddPaymentMethod: 'AddPaymentMethodPage',
};

/**
 * Map of routes that are dependent on the user status for conditional
 * redirection or other protection. The method return dictates the
 * behavior:
 * 	- true: allow access
 * 	- false: do not load the route
 * 	- string: redirect to the route named in the string
 */
export const dataRouteProtectionCustom: Partial<
	Record<
		keyof LocalRoutes,
		(user: Partial<User>, _cart?: Order | boolean, settings?: Settings) => RouteProtection
	>
> = {
	CreateCheckoutProfiles: (user, _cart, settings) =>
		validateProtectedRoute({ user, settings }, ['login', 'account/checkout-profiles']),
	EditCheckoutProfile: (user, _cart, settings) =>
		validateProtectedRoute({ user, settings }, ['login', 'account/checkout-profiles']),
	PaymentMethod: (user) => validateProtectedRoute({ user }, 'login'),
	AddPaymentMethod: (user) => validateProtectedRoute({ user }, 'login'),
};

export const dataRouteProtectionFlexFlowMapCustom: Partial<Record<keyof LocalRoutes, string>> = {};

export const notCDNCacheableRouteCustom: Partial<Record<keyof LocalRoutes, boolean>> = {};
